import { Capacitor } from '@capacitor/core';
import { useCallback, useEffect } from 'react';
import { atom, useRecoilState } from 'recoil';
import { localStorageEffect } from 'libs/recoil';
import {
  LOCAL_STORAGE_KEY,
  getLastPushNotificationTimestamp,
} from './localStorage';

type OnboardingState = {
  firstAppOpen: boolean;
  onboardUser: boolean;
  onboardCompleted: boolean;
  notificationsEnabled: boolean;
  showTutorialSteps: boolean;
  criticalAlertsRequested: boolean;
  webFirstOpenedAt?: string;
  nativeOnboardedAt?: string;
};

type UseOnboardingState = {
  showTutorialSteps: boolean;
  setFirstAppOpen: () => void;
  setOnboardUser: (onboardUser: boolean) => void;
  setCompleted: (onboardCompleted: boolean) => void;
  setNotificationsEnabled: (notificationsEnabled: boolean) => void;
  setCriticalAlertsRequested: (criticalAlertsRequested: boolean) => void;
  onboardCompleted: boolean;
  onboardUser: boolean;
  notificationsEnabled: boolean;
  criticalAlertsRequested: boolean;
  webFirstOpenedAt?: string | undefined;
  nativeOnboardedAt?: string | undefined;
  combinedOnboardedAt?: string | undefined;
  setShowTutorialSteps: (showTutorialSteps: boolean) => void;
};

type LegacyOnboardingState = OnboardingState & {
  onboardedAt?: string;
};

const FIVE_MINUTES_MS = 1000 * 60 * 5; // 5 minutes

export const initialState: OnboardingState = {
  firstAppOpen: true,
  onboardUser: true,
  onboardCompleted: false,
  notificationsEnabled: false,
  showTutorialSteps: true,
  criticalAlertsRequested: false,
};

// Merges initial state and saved state object values.
// Useful when adding new properties that might not
// exist in the saved state object.
const mergeValue = (savedValue: LegacyOnboardingState): OnboardingState => {
  const { onboardedAt, ...mergedValue } = savedValue;

  return {
    ...initialState,
    ...mergedValue,
    nativeOnboardedAt: mergedValue.nativeOnboardedAt ?? onboardedAt,
  };
};

export const onboardingStateAtom = atom({
  key: LOCAL_STORAGE_KEY.ONBOARDING_STATE,
  default: { ...initialState },
  effects: [localStorageEffect(LOCAL_STORAGE_KEY.ONBOARDING_STATE, mergeValue)],
});

export const useOnboardingState = (): UseOnboardingState => {
  const [onboardingState, setOnboardingState] =
    useRecoilState(onboardingStateAtom);

  const setWebFirstOpenedAt = useCallback(() => {
    setOnboardingState((currVal) => {
      if (currVal.webFirstOpenedAt) {
        return currVal;
      }

      return { ...currVal, webFirstOpenedAt: new Date().toISOString() };
    });
  }, [setOnboardingState]);

  useEffect(() => {
    if (Capacitor.isNativePlatform()) {
      return;
    }

    setWebFirstOpenedAt();
  }, [setWebFirstOpenedAt]);

  const setFirstAppOpen = useCallback(() => {
    setOnboardingState((currVal) => ({ ...currVal, firstAppOpen: false }));
  }, [setOnboardingState]);

  const setOnboardUser = useCallback(
    (onboardUser: boolean) => {
      setOnboardingState((currVal) => {
        // eslint-disable-next-line prefer-destructuring
        let onboardCompleted = currVal.onboardCompleted;

        if (!currVal.onboardCompleted && !onboardUser) {
          onboardCompleted = true;
        }

        const nativeOnboardedAt = onboardCompleted
          ? new Date().toISOString()
          : undefined;

        return {
          ...currVal,
          onboardUser,
          onboardCompleted,
          nativeOnboardedAt,
        };
      });
    },
    [setOnboardingState],
  );

  const setCompleted = useCallback(
    (onboardCompleted: boolean) => {
      setOnboardingState((currVal) => ({
        ...currVal,
        onboardCompleted,
        onboardUser: false,
        nativeOnboardedAt: new Date().toISOString(),
      }));
    },
    [setOnboardingState],
  );

  const setNotificationsEnabled = useCallback(
    (notificationsEnabled: boolean) => {
      setOnboardingState((currVal) => ({ ...currVal, notificationsEnabled }));
    },
    [setOnboardingState],
  );

  const setShowTutorialSteps = useCallback(
    (showTutorialSteps: boolean) => {
      setOnboardingState((currVal) => ({ ...currVal, showTutorialSteps }));
    },
    [setOnboardingState],
  );

  const setCriticalAlertsRequested = useCallback(
    (criticalAlertsRequested: boolean) => {
      setOnboardingState((currVal) => ({
        ...currVal,
        criticalAlertsRequested,
      }));
    },
    [setOnboardingState],
  );

  const lastPushNotificationTimestamp = getLastPushNotificationTimestamp();

  const showTutorialSteps =
    !onboardingState.onboardUser &&
    onboardingState.showTutorialSteps &&
    Capacitor.isNativePlatform() &&
    Date.now() - lastPushNotificationTimestamp > FIVE_MINUTES_MS;
  const combinedOnboardedAt = Capacitor.isNativePlatform()
    ? onboardingState.nativeOnboardedAt
    : onboardingState.webFirstOpenedAt;

  return {
    ...onboardingState,
    combinedOnboardedAt,
    showTutorialSteps,
    setFirstAppOpen,
    setOnboardUser,
    setCompleted,
    setNotificationsEnabled,
    setShowTutorialSteps,
    setCriticalAlertsRequested,
  };
};
